import {Tag, TagColor, TagSize} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import Backbone from 'backbone';
import 'moment-timezone';
import React, {useEffect, useState} from 'react';
import ReactDOM from 'react-dom';

import BenchlingConfig from 'BenchlingConfig';
import DataConnector from 'connect/DataConnector';
import {EXTERNAL_FILE_TYPE} from 'constants/PreviewTypeConstants';
import ModelHubExecutionCountNotice from 'model-hub/ModelHubExecutionCountNotice';
import {
  getModelDisplayName,
  ScientificModelPreferences,
} from 'model-hub/scientific-models/ScientificModelConstants';
import QualityDataLegend from 'molbio/proteins/alphafold/QualityDataLegend';
import ProteinStore from 'molbio/proteins/ProteinStore';
import {ProteinStructureExternalFile} from 'molbio/proteins/util/ProteinConstants';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER} from 'ui-library/Colors';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import usePreference from 'ui-library/hooks/usePreference';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import AlphaFoldTabContent from './AlphaFoldTabContent';
import AlphaFoldTabState from './AlphaFoldTabState';
import {
  downloadStructureFile,
  findCoordinateStructureById,
  getStructuresForVersion,
  getStructureInfoFromVersions,
} from './AlphaFoldTabUtils';
import StructurePredictionInfo from './StructurePredictionInfo';

class AlphaFoldOptionsConnector extends DataConnector<
  {proteinId: string},
  {
    structureFile: ProteinStructureExternalFile | null;
    model: string | null;
    modelVersion: string | null;
  }
> {
  override shouldSubscribeToRedux = false;
  override stores = [ProteinStore];

  // Track the last seen version ID to detect version changes
  private lastSeenVersionId: number | null = null;

  enhanceProps(): {
    structureFile: ProteinStructureExternalFile | null;
    model: string | null;
    modelVersion: string | null;
    proteinId: string;
    structureProteinVersionId: number | null;
  } {
    const proteinStore = this.proteinStore();
    const protein = proteinStore.getProteinById(this.props.proteinId)!;
    const proteinId = this.props.proteinId;
    const currentlyViewedProteinVersionId = protein.version.id;

    // Track if this is the first time we're seeing this version
    const versionChanged = this.lastSeenVersionId !== currentlyViewedProteinVersionId;
    this.lastSeenVersionId = currentlyViewedProteinVersionId;

    const versions = proteinStore.getVersionsById(proteinId);
    const currentCoordinateStructureId = proteinStore.getCoordinateStructureId(
      currentlyViewedProteinVersionId
    );

    // If there's a current coordinate structure ID, use that specific prediction
    if (currentCoordinateStructureId) {
      const allCoordinateStructures = getStructuresForVersion(versions, currentlyViewedProteinVersionId);
      const selectedCoordinateStructure = findCoordinateStructureById(
        allCoordinateStructures,
        currentCoordinateStructureId
      );

      if (selectedCoordinateStructure) {
        return {
          structureFile: selectedCoordinateStructure.structureFile,
          model: selectedCoordinateStructure.model,
          modelVersion: selectedCoordinateStructure.modelVersion,
          proteinId,
          structureProteinVersionId: selectedCoordinateStructure.versionId,
        };
      }
    }

    // Fallback to getStructureInfoFromVersions when switching to a version for the first time
    // This handles initial load and version switching
    if (versionChanged) {
      const structureInfo = getStructureInfoFromVersions(currentlyViewedProteinVersionId, versions);
      return {
        structureFile: structureInfo.structureFile,
        model: structureInfo.model,
        modelVersion: structureInfo.modelVersion,
        proteinId,
        structureProteinVersionId: structureInfo.versionId,
      };
    }

    // No current prediction and we've seen this version before - return null values
    return {
      structureFile: null,
      model: null,
      modelVersion: null,
      proteinId,
      structureProteinVersionId: null,
    };
  }
}

export function AlphaFoldOptions({
  structureFile,
  model,
  modelVersion,
  proteinId,
  structureProteinVersionId,
}: {
  structureFile: ProteinStructureExternalFile | null;
  model: string | null;
  modelVersion: string | null;
  proteinId: string;
  structureProteinVersionId: number | null;
}): JSX.Element {
  const [hasDismissedPopover, setHasDismissedPopover] = usePreference<boolean>(
    ScientificModelPreferences.HAS_DISMISSED_MODEL_CONFIDENCE_POPOVER
  );
  const [popoverProps, setPopoverProps] = useState<
    {isOpen: boolean; interaction: FloatingInteraction.CONTROLLED} | {interaction: FloatingInteraction.HOVER}
  >(() => {
    // If user has already dismissed the popover, start with hover interaction
    if (hasDismissedPopover) {
      return {interaction: FloatingInteraction.HOVER};
    }
    return {
      isOpen: true,
      interaction: FloatingInteraction.CONTROLLED,
    };
  });

  useEffect(() => {
    // Only set the timeout if the user hasn't dismissed the popover before
    if (!hasDismissedPopover) {
      const timeoutId = setTimeout(() => {
        setPopoverProps({interaction: FloatingInteraction.HOVER});
      }, 5000);
      return () => {
        clearTimeout(timeoutId);
      };
    }
    return () => {};
  }, [hasDismissedPopover]);

  const handleDismissPopover = (): void => {
    setHasDismissedPopover(true);
    setPopoverProps({interaction: FloatingInteraction.HOVER});
  };

  if (structureFile != null && model != null && modelVersion != null && structureProteinVersionId != null) {
    return (
      <div className={css(styles.optionButtonsLayout)}>
        <div className={css(styles.leftSection)}>
          <div>
            <Popover
              {...popoverProps}
              placement={FloatingPlacement.RIGHT_START}
              renderContent={() => (
                <QualityDataLegend
                  onDismiss={
                    popoverProps.interaction === FloatingInteraction.CONTROLLED
                      ? handleDismissPopover
                      : undefined
                  }
                />
              )}
            >
              <BenchlingIcon icon="info-circle" className={css(styles.infoIcon)} />
            </Popover>
            <span className={css(styles.infoText)}>Model confidence</span>
          </div>
          <StructurePredictionInfo
            modelDisplayName={getModelDisplayName(model)}
            modelVersion={modelVersion}
            className={css(styles.predictionInfo)}
          />
        </div>
        <Button
          className={css(styles.withoutButtonFocusStyling)}
          text={`Download ${
            structureFile.previewType === EXTERNAL_FILE_TYPE.MOLSTAR_MMCIF_VIEWER ? 'mmCIF (PDBx)' : 'PDB'
          }`}
          buttonStyle={ButtonStyle.PRIMARY}
          size={ButtonSize.MINI}
          onClick={() => downloadStructureFile(structureFile, proteinId, structureProteinVersionId)}
        />
      </div>
    );
  } else if (BenchlingConfig.MOLBIO_ALPHAFOLD_IS_ACADEMIC) {
    return <ModelHubExecutionCountNotice inBanner={false} />;
  } else {
    return (
      <div className={css(styles.optionButtonsLayout)}>
        <span className={css(styles.previewBadgeContainer)}>
          <Tag size={TagSize.SMALL} color={TagColor.PURPLE} text="PREVIEW" />
        </span>
      </div>
    );
  }
}

const ConnectedAlphaFoldOptions = AlphaFoldOptionsConnector.connect(AlphaFoldOptions);

class AlphaFoldOptionsView extends Backbone.View<AlphaFoldTabState> {
  override render(): this {
    const proteinId = this.model.protein().id;
    renderToDOM(<ConnectedAlphaFoldOptions proteinId={proteinId} />, this.el);
    return this;
  }

  override remove(): Backbone.View<AlphaFoldTabState> {
    ReactDOM.unmountComponentAtNode(this.el);
    return super.remove();
  }
}

class AlphaFoldTabView extends Backbone.View<AlphaFoldTabState> {
  static initClass(): void {
    this.prototype.className = 'buffer-tab buffer-tab--no-padding';
  }

  override render(): this {
    renderToDOM(<AlphaFoldTabContent proteinId={this.model.protein().id} />, this.el);
    return this;
  }

  override remove(): Backbone.View<AlphaFoldTabState> {
    ReactDOM.unmountComponentAtNode(this.el);
    return super.remove();
  }
}

AlphaFoldTabView.initClass();

const styles = StyleSheet.create({
  infoIcon: {
    color: GRAY_DARKER,
  },
  infoText: {
    ...FONT_PROXIMA_13,
    marginLeft: '8px',
  },
  previewBadgeContainer: {
    marginLeft: '8px',
  },
  optionButtonsLayout: {
    padding: '0px 7px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    // Should match the value inherited from .buffer-options
    height: '35px',
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
  predictionInfo: {
    margin: '0',
  },
  largerText: FONT_PROXIMA_13,
  withoutButtonFocusStyling: {':focus': {textDecoration: 'none'}},
});

export default {tabView: AlphaFoldTabView, optionsView: AlphaFoldOptionsView};
